import { useEffect, useMemo, useRef, useState } from "react";
import { FaChevronRight, FaCloudUploadAlt, FaSearch, FaUserTie } from "react-icons/fa";
import Select from "react-select";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import i18n from "../../../i18n";
import {
    deleteOrganizationDocument,
    downloadOrganizationDocument,
    fetchFolderDocuments,
    fetchOrganizationDocumentVersions,
    uploadFolderDocument,
    uploadOrganizationDocumentVersion,
} from "../../../redux/slices/organizationDocumentsSlice";
import {
    EmptyState,
    FileTable,
    SectionTitle,
    VersionDialog,
    scopeIcon,
} from "../../../components/documentLibrary/DocumentViews";

export const ALL_EMPLOYEES = "__all__";
const ACCEPTED_FILES = ".pdf,.png,.jpg,.jpeg,.gif,.webp,.txt,.csv,.rtf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.odt,.ods,.odp";

const t = (key, options) => i18n.t(`pages.OrganizationDocuments.${key}`, options);

const selectStyles = {
    control: (base, state) => ({
        ...base,
        minHeight: 40,
        backgroundColor: "#fff",
        borderRadius: 7,
        border: state.isFocused ? "1px solid var(--documents-navy)" : "1px solid #d0d5dd",
        boxShadow: "none",
        "&:hover": { border: "1px solid var(--documents-navy)" },
    }),
    menu: (base) => ({ ...base, zIndex: 5 }),
    option: (base, state) => ({
        ...base,
        backgroundColor: state.isFocused ? "var(--documents-wash)" : "#fff",
        color: "var(--documents-ink)",
    }),
};

const FolderContents = ({ folder, users, accessSummary, onBack }) => {
    const dispatch = useDispatch();
    const { folderDocuments, folderDocumentsLoading, versionsByDocument } = useSelector((state) => state.organizationDocuments);
    const uploadInput = useRef(null);
    const versionInput = useRef(null);
    const versionTarget = useRef(null);
    const [search, setSearch] = useState("");
    const [versionDocument, setVersionDocument] = useState(null);

    const isEmployeeFolder = folder.scope === "employee";
    const [recipient, setRecipient] = useState(
        isEmployeeFolder && folder.allow_shared_documents ? ALL_EMPLOYEES : null,
    );
    // Company folders need no recipient; employee folders need "all employees" or one employee.
    const ready = !isEmployeeFolder || recipient !== null;
    const employeeId = recipient && recipient !== ALL_EMPLOYEES ? recipient : null;

    const recipientOptions = useMemo(() => [
        ...(folder.allow_shared_documents ? [{ value: ALL_EMPLOYEES, label: t("all_employees") }] : []),
        ...users.map((user) => ({ value: user.id, label: user.name })),
    ], [folder.allow_shared_documents, users]);

    const reload = () => dispatch(fetchFolderDocuments({ folderId: folder.id, employeeId }));

    useEffect(() => {
        if (ready) dispatch(fetchFolderDocuments({ folderId: folder.id, employeeId }));
    }, [dispatch, folder.id, employeeId, ready]);

    const run = async (action, successKey, refresh = true) => {
        try {
            await dispatch(action).unwrap();
            if (successKey) toast.success(t(successKey));
            if (refresh) reload();
        } catch (error) {
            toast.error(typeof error === "string" ? error : t("generic_error"));
        }
    };

    const handleUpload = async (event) => {
        const files = Array.from(event.target.files || []);
        event.target.value = "";
        for (const file of files) {
            await run(uploadFolderDocument({ folderId: folder.id, employeeId, file }), "file_uploaded", false);
        }
        reload();
    };

    const handleVersion = (event) => {
        const [file] = event.target.files || [];
        event.target.value = "";
        if (file && versionTarget.current) {
            run(uploadOrganizationDocumentVersion({ documentId: versionTarget.current, file }), "version_uploaded");
        }
    };

    const openVersions = (document) => {
        setVersionDocument(document);
        run(fetchOrganizationDocumentVersions(document.id), null, false);
    };

    const handleDelete = (document) => {
        if (window.confirm(t("confirm_delete_file", { name: document.title }))) {
            run(deleteOrganizationDocument(document.id), "file_deleted", false);
        }
    };

    const shownDocuments = useMemo(
        () => folderDocuments.filter((document) => document.title.toLowerCase().includes(search.toLowerCase())),
        [folderDocuments, search],
    );

    const audience = !isEmployeeFolder
        ? accessSummary
        : recipient === ALL_EMPLOYEES
            ? t("audience_all_employees")
            : employeeId
                ? t("audience_one_employee", { name: recipientOptions.find((option) => option.value === employeeId)?.label })
                : null;

    return (
        <div className="document-library document-library--embedded">
            <div className="document-workspace document-workspace--single">
                <section className="document-browser">
                    <div className="document-browser-toolbar">
                        <nav className="document-breadcrumbs" aria-label={i18n.t("pages.Documents.breadcrumbs")}>
                            <button onClick={onBack}>{t("back_to_folders")}</button>
                            <span><FaChevronRight /><button onClick={reload}>{folder.name}</button></span>
                        </nav>
                        <div className="document-toolbar-actions">
                            {isEmployeeFolder && (
                                <div className="document-recipient">
                                    <label htmlFor="org-documents-recipient" className="visually-hidden">{t("recipient")}</label>
                                    <Select
                                        inputId="org-documents-recipient"
                                        options={recipientOptions}
                                        value={recipientOptions.find((option) => option.value === recipient) || null}
                                        onChange={(option) => setRecipient(option?.value ?? null)}
                                        placeholder={t("recipient_placeholder")}
                                        noOptionsMessage={() => t("no_employees")}
                                        styles={selectStyles}
                                    />
                                </div>
                            )}
                            <label className="document-search">
                                <FaSearch />
                                <span className="visually-hidden">{i18n.t("pages.Documents.search")}</span>
                                <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={i18n.t("pages.Documents.searchPlaceholder")} />
                            </label>
                            {ready && (
                                <button className="document-button document-button--primary" onClick={() => uploadInput.current?.click()}>
                                    <FaCloudUploadAlt />{t("upload_files")}
                                </button>
                            )}
                            <input ref={uploadInput} type="file" multiple hidden accept={ACCEPTED_FILES} onChange={handleUpload} data-testid="org-documents-upload" />
                            <input ref={versionInput} type="file" hidden accept={ACCEPTED_FILES} onChange={handleVersion} />
                        </div>
                    </div>

                    <div className="document-folder-heading">
                        <div>
                            <span>{scopeIcon[folder.scope]}</span>
                            <div>
                                <h2>{folder.name}</h2>
                                <p>{audience ? t("visible_to", { audience }) : t(`scope_${folder.scope}`)}</p>
                            </div>
                        </div>
                    </div>
                    {isEmployeeFolder && !folder.allow_shared_documents && <p className="document-dialog-lead">{t("shared_disabled_hint")}</p>}

                    {!ready ? (
                        <EmptyState icon={<FaUserTie />} title={t("pick_recipient")} hint="" />
                    ) : (
                        <>
                            <SectionTitle className="document-files-heading" title={i18n.t("pages.Documents.files")} count={shownDocuments.length} />
                            {folderDocumentsLoading ? (
                                <div className="document-loading">{t("loading")}</div>
                            ) : shownDocuments.length ? (
                                <FileTable
                                    documents={shownDocuments}
                                    onDownload={(document) => run(downloadOrganizationDocument({ documentId: document.id }), null, false)}
                                    onVersions={openVersions}
                                    onNewVersion={(document) => { versionTarget.current = document.id; versionInput.current?.click(); }}
                                    onDelete={handleDelete}
                                    renderNote={(document) => document.shared && t("audience_all_employees")}
                                />
                            ) : (
                                <EmptyState
                                    className="document-empty--files"
                                    icon={<FaCloudUploadAlt />}
                                    title={t("no_files")}
                                    hint={i18n.t("pages.Documents.noFilesHint")}
                                />
                            )}
                        </>
                    )}
                </section>
            </div>

            {versionDocument && (
                <VersionDialog
                    document={versionDocument}
                    versions={versionsByDocument[versionDocument.id] || []}
                    onClose={() => setVersionDocument(null)}
                    onDownload={(versionId) => run(downloadOrganizationDocument({ documentId: versionDocument.id, versionId }), null, false)}
                />
            )}
        </div>
    );
};

export default FolderContents;
